<template>
    <th
        class="uik-table-head-cell"
        :class="{
            'uik-table-head-cell__hide--desktop-small': hide === 'd',
            'uik-table-head-cell__hide--tablet': hide === 't',
            'uik-table-head-cell__hide--mobile': hide === 'm',
            'uik-table-head-cell--clickable': sortingEnabled || filtersEnabled,
            'uik-table-head-cell--sortable': sortingEnabled,
            'uik-table-head-cell--filterable': filtersEnabled,
            'uik-table-head-cell--sort-active': computedSortDirection && sortingEnabled,
            'uik-table-head-cell--loading': getLoading,
            'uik-table-head-cell--filters-open': filtersOpen,
            'uik-table-head-cell--filter-active': filterActive
        }"
        :title="column || sortable || filterable ? '' : getTitle"
    >
        <div
            class="uik-table-head-cell__container"
            :class="{
                'uik-table-head-cell__align--center': align === 'center',
                'uik-table-head-cell__align--right': align === 'right',
                'uik-table-head-cell__text--small': isStyle('small'),
                'uik-table-head-cell__text--fade': isStyle('fade'),
                'uik-table-head-cell__text--base': isStyle('base'),
                'uik-table-head-cell__text--code': isStyle('code'),
                'uik-table-head-cell__text--bold': isStyle('bold'),
                'uik-table-head-cell__text--primary': isStyle('primary')
            }"
            tabindex="0"
            @click="handleClick()"
        >
            <div class="uik-table-head-cell__content">
                <slot/>
            </div>
            <button
                v-if="filtersEnabled"
                class="uik-table-head-cell__filter-icon"
                title="Filter"
                @click.stop="openFilters()"
            >
                <UikIcon :icon="['fas', 'filter']"/>
            </button>
            <div
                v-if="sortingEnabled"
                class="uik-table-head-cell__sort-icon"
                :class="{'uik-table-head-cell__sort-icon--up': computedSortDirection === 'up' && sortingEnabled}"
            >
                <UikIcon :icon="['fas', 'sort-down']"/>
            </div>
        </div>
        <UikFilters
            v-if="filtersEnabled"
            :open="filtersOpen"
            :activeData="activeFilterData"
            :data="filterData"
            :type="filterType"
            :title="getTitle"
            :remaps="filterRemaps"
            :isLoading="filterLoading || ($api && $api.FilterOptions && $api.FilterOptions.loaders ? $api.FilterOptions.loaders.includes(this.filterOptionsNamespace) : false)"
            :nullOptionMessage="filterNullOptionMessage"
            :step="Number(filterStep)"
            @close="handleFilterApply($event)"
        />
    </th>
</template>

<script>
import UikFilters from "./Filters"

function formatDate (date) {
    let d = new Date(date)
    let month = "" + (d.getMonth() + 1)
    let day = "" + d.getDate()
    let year = d.getFullYear()

    if (month.length < 2) { month = "0" + month }
    if (day.length < 2) { day = "0" + day }

    return [year, month, day].join("-")
}
export default {
    name: "UikTh",
    components: { UikFilters },
    props: {
        /**
         * Defines alignment of content
         */
        align: { type: String, default: "left" },
        /**
         * Style properties of plain text inside the cell
         */
        text: { type: [Array, String], default: "" },
        /**
         * Defines on which responsive breakpoint cell disappears
         */
        hide: { type: String, default: "" },
        /**
         * Element's title property
         */
        title: { type: String, default: "" },
        /**
         * Is this header sortable?
         */
        sortable: { type: Boolean, default: false },
        /**
         * Is this header filterable?
         */
        filterable: { type: Boolean, default: false },
        /**
         * The current sort direction
         */
        sortDirection: { type: [String, null], default: null },
        /**
         * The current sort direction
         */
        sortByCount: { type: Boolean, default: false },
        /**
         * Is the sort header loading?
         */
        isLoading: { type: [Boolean, null], default: null },
        /**
         * The loader type
         */
        loaderType: { type: String, default: "spinner-fade" },
        /**
         * The color of the loader
         */
        loaderColor: { type: String, default: "primary" },
        /**
         * The size of the loader
         */
        loaderSize: { type: Number, default: 0.3 },
        /**
         * The column to sort/filter by
         */
        column: { type: [String, null], default: null },
        /**
         * The column to filter by
         */
        filterColumn: { type: [String, null], default: null },
        /**
         * The column to sort by
         */
        sortColumn: { type: [String, null], default: null },
        /**
         * The filter options to display in the filter
         */
        filterOptions: { type: [Object, Array, null], default: null },
        /**
         * The selected or active filter options to display in the filter
         */
        filterActiveOptions: { type: [Array, null], default: null },
        /**
         * The filter type
         */
        filterType: { type: [String, null], default: null },
        /**
         * An object that defines which options fields should be remaped to what
         */
        filterRemaps: { type: [Object, null], default: null },
        /**
         * The module namespace under which the filters will be saved (route name by default)
         */
        filterModuleNamespace: { type: [String, null], default: null },
        /**
         * A custom key used for saving filters in the resource store
         */
        filterKey: { type: [String, null], default: null },
        /**
         * Shoulf the filters be in a loading state
         */
        filterLoading: { type: Boolean, default: false },
        /**
         * A custom getFilterOptions method
         */
        getFilterOptionsHandler: { type: [Function, null], default: null },
        /**
         * A custom key (namespace) used for saving filter options in the FilterOptions store
         */
        filterOptionsParams: {
            type: [Object, null],
            default: null,
            validator: value => {
                if (
                    !value.hasOwnProperty("type") ||
                    !value.hasOwnProperty("model") ||
                    ![
                        "list",
                        "relationList",
                        "minMax",
                        "polymorphicRelationList",
                        "relationCount"
                    ].includes(value.type)
                ) {
                    // eslint-disable-next-line no-console
                    console.error("Invalid filter options structure, reason: !value.hasOwnProperty(\"type\") || !value.hasOwnProperty(\"model\") || ![\"list\", \"relationList\", \"minMax\", \"polymorphicRelationList\", \"relationCount\"].includes(value.type)", value)
                    return false
                }

                if (value.type === "list" || value.type === "minMax" || value.type === "polymorphicRelationList") {
                    if (!value.hasOwnProperty("column") && !value.hasOwnProperty("columns")) {
                        // eslint-disable-next-line no-console
                        console.error("Invalid filter options structure, reason: !value.hasOwnProperty(\"column\") && !value.hasOwnProperty(\"columns\")", value)
                        return false
                    }
                } else if (value.type === "relationList") {
                    if (
                        !value.hasOwnProperty("columns") ||
                        !value.hasOwnProperty("relation") ||
                        !value.hasOwnProperty("inverseRelation")
                    ) {
                        // eslint-disable-next-line no-console
                        console.error("Invalid filter options structure, reason: (!value.hasOwnProperty(\"columns\") || !value.hasOwnProperty(\"relation\") || !value.hasOwnProperty(\"inverseRelation\"))", value)
                        return false
                    }
                }
                return true
            }
        },
        /**
         * Filters null value option title message
         */
        filterNullOptionMessage: { type: [String, null], default: null },
        /**
         * A step value for the slider (only for number filter)
         */
        filterStep: { type: [Number, String, null], default: null },
        /**
         * Does the column represent a relation? Is so, the column name will be used
         * as the name of the relationship unless overriden with relation prop
         */
        isRelation: { type: Boolean, default: false },
        /**
         * Does the column represent a polymorphic relation? Is so, the column name will be used
         * as the name of the relationship unless overriden with relation prop
         */
        isPolymorphicRelation: { type: Boolean, default: false },
        /**
         * If provided, serves as the name of the relationship this filter is for
         * If Company has a relationship with User this might be users or responsible
         */
        relation: { type: [String, null], default: null },
        /**
         * The name of the column the selected data represents (id by default)
         */
        relationKey: { type: String, default: "id" },
        /**
         * Should filtering and sorting be disabled even when provided with params?
         */
        disabled: { type: Boolean, default: false }
    },
    inject: ["resource"],
    data () {
        return {
            filtersOpen: false,
            loading: false
        }
    },
    computed: {
        /**
         * The active column for sorting operations
         */
        activeSortColumn () {
            return this.sortColumn || this.column
        },
        /**
         * The active column for filtering operations
         */
        activeFilterColumn () {
            return this.filterColumn || this.column || (this.isRelation ? this.relation : null)
        },
        /**
         * Based on prop values should filters be enabled?
         */
        filtersEnabled () {
            return (this.filterable || (this.activeFilterColumn && !this.sortable) || (this.isRelation && this.relation && this.filterType)) && !this.disabled
        },
        /**
         * Based on prop values should sorting be enabled?
         */
        sortingEnabled () {
            return (this.sortable || (this.activeSortColumn && !this.filterable) || (this.isRelation && this.relation && this.sortByCount)) && !this.disabled
        },
        /**
         * Module namespace the filter will be and is saved under
         */
        computedFilterModuleNamespace () {
            return this.filtersModuleNamespace || this.filterModuleNamespace || this.$route.name
        },
        /**
         * Get element title based on text in slot or defined title
         *
         * @return { String }
         */
        getTitle () {
            if (this.title) return this.title
            if (this.$slots.default) {
                let textNode = this.$slots.default.find(item => item.text)
                if (textNode) return textNode.text
            }
            return ""
        },
        /**
         * Namespace the filter options will be and are saved under
         */
        filterOptionsNamespace () {
            return this.activeFilterColumn || (this.filterOptionsParams ? (this.filterOptionsParams.namespace || this.filterOptionsParams.column || this.filterOptionsParams.columns[0]) : this._uid)
        },
        /**
         * Get the filter options from the prop or the resource
         *
         * @return { Array } an array of filter options
         */
        filterData () {
            if (this.filterOptions) return this.filterOptions
            if (!this.$api || !this.$api.FilterOptions || !this.$api.FilterOptions.options) return []
            let data = this.$api.FilterOptions.options[this.filterOptionsNamespace]
            if (this.filterType === "number" && data) {
                let stepDecimalsCount = this.filterStep && String(this.filterStep).includes(".") ? String(this.filterStep).split(".")[1].length || 0 : 0
                return { min: data.min === null ? 0 : Number(data.min).toFixed(stepDecimalsCount), max: data.max === null ? 0 : Number(data.max).toFixed(stepDecimalsCount) }
            }
            return data
        },
        /**
         * Get the active sort direction from the prop or the resource
         *
         * @return { String } active sort direction
         */
        computedSortDirection () {
            if (this.sortDirection) return this.sortDirection
            if (this.resource) {
                if (!this.resource.state.listSortParam) return null
                let sortString
                if (this.isRelation && this.relation) {
                    if (this.sortByCount) {
                        let countParam = this.sortByCount ? "|count" : ""
                        sortString = `${this.relation}|relation${countParam}`
                    } else {
                        sortString = `${this.activeSortColumn}|relation:${this.relation}`
                    }
                } else {
                    sortString = this.activeSortColumn
                }
                if (this.resource.state.listSortParam.split(",").includes(sortString)) {
                    return "down"
                } else if (this.resource.state.listSortParam.split(",").includes(`-${sortString}`)) {
                    return "up"
                }
            }
            return null
        },
        /**
         * Get filter state
         *
         * @return { Boolean } is the filter active?
         */
        filterActive () {
            if (this.filterActiveOptions && this.filterActiveOptions.length > 0) return true
            if (!this.resource) return false
            if (!this.resource.state.filters[this.computedFilterModuleNamespace]) return false
            return !!this.resource.state.filters[this.computedFilterModuleNamespace][this.activeFilterColumn]
        },
        /**
         * Get the active/selected filter options from the prop or the resource
         *
         * @return { Array } an array of active/selected filter options
         */
        activeFilterData () {
            let abort = () => {
                if (this.filterType === "date" && this.filterData) {
                    return [this.filterData[this.filterRemaps ? this.filterRemaps.min : "min"], this.filterData[this.filterRemaps ? this.filterRemaps.max : "max"]]
                }
                return []
            }
            if (this.filterActiveOptions) return this.filterActiveOptions
            if (!this.resource) return abort()
            if (!this.resource.state.filters[this.computedFilterModuleNamespace]) return abort()
            if (!this.resource.state.filters[this.computedFilterModuleNamespace][this.activeFilterColumn]) return abort()
            let filter = this.resource.state.filters[this.computedFilterModuleNamespace][this.activeFilterColumn]
            if (this.isRelation) {
                if (this.filterType === "date") {
                    if (!filter.whereHas || filter.whereHas.length < 2 || !filter.whereHas[1].whereBetween || filter.whereHas[1].whereBetween.length < 2) return []
                    return filter.whereHas[1].whereBetween[1]
                }
                if (this.filterType === "number") {
                    if (!filter.whereHas || filter.whereHas.length < 4 || !filter.has || filter.has.length < 3) return []
                    return [filter.whereHas[3], filter.has[2]]
                }
                if (this.filterType === "list" || this.filterType === "userList") {
                    // eslint-disable-next-line no-console
                    if (((!filter.whereHas && !filter.orDoesntHave) && !filter.doesntHave)) {
                        // eslint-disable-next-line no-console
                        console.error("Invalid filter structure: reason '(!filter.whereHas && !filter.orDoesntHave) && !filter.doesntHave'", filter)
                        return []
                    }
                    if (!filter.whereHas) return [null]
                    if (filter.whereHas.length < 2) {
                        // eslint-disable-next-line no-console
                        console.error("Invalid filter structure: reason 'filter.whereHas.length < 2'", filter)
                        return []
                    }
                    if (!filter.whereHas[1].whereIn) {
                        // eslint-disable-next-line no-console
                        console.error("Invalid filter structure: reason '!filter.whereHas[1].whereIn'", filter)
                        return []
                    }
                    if (filter.whereHas[1].whereIn.length < 2) {
                        // eslint-disable-next-line no-console
                        console.error("Invalid filter structure: reason 'filter.whereHas[1].whereIn.length < 2'", filter)
                        return []
                    }
                    if (!filter.orDoesntHave) return filter.whereHas[1].whereIn[1]
                    return [null, ...filter.whereHas[1].whereIn[1]]
                }
            }
            if (this.isPolymorphicRelation) {
                if (!filter.whereHasMorphIn) {
                    // eslint-disable-next-line no-console
                    console.error("Invalid filter structure: reason '!filter.whereHasMorphIn'", filter)
                    return []
                }
                if (filter.whereHasMorphIn.length < 2 || !(typeof filter.whereHasMorphIn[1] === "object" && filter.whereHasMorphIn[1] !== null)) {
                    // eslint-disable-next-line no-console
                    console.error("Invalid filter structure: reason `filter.whereHasMorphIn.length < 2 || !(typeof filter.whereHasMorphIn[1] === 'object' && filter.whereHasMorphIn[1] !== null)`", filter)
                    return []
                }
                let output = []
                Object.keys(filter.whereHasMorphIn[1]).forEach(model => {
                    filter.whereHasMorphIn[1][model].forEach(id => {
                        output.push({ model, id })
                    })
                })
                return output
            }
            if (this.filterType === "date" || this.filterType === "number") {
                if (!filter.whereBetween || filter.whereBetween.length < 2) return []
                return filter.whereBetween[1]
            }
            if (this.filterType === "list" || this.filterType === "userList") {
                if ((!filter.whereIn && !filter.orWhereNull)) return []
                if (!filter.whereIn) return [null]
                if (filter.whereIn.length < 2) {
                    // eslint-disable-next-line no-console
                    console.error("Invalid filter structure: reason 'filter.whereIn.length < 2'")
                    return []
                }
                if (!filter.orWhereNull) return filter.whereIn[1]
                return [null, ...filter.whereIn[1]]
            }
            return []
        },
        /**
         * Get the loading state from the prop or internal state
         *
         * @return { Boolean } is the header in loading state?
         */
        getLoading () {
            if (this.isLoading !== null) return this.isLoading
            return this.loading
        }
    },
    methods: {
        /**
         * Checks if plain text has a preset style
         *
         * @param { String } - style to check
         *
         * @return { Boolean }
         */
        isStyle (style) {
            return this.text === style || (Array.isArray(this.text) && this.text.includes(style))
        },
        /**
         * On click emits toggleSort,
         * changes sort direction if the header is sortable and a resource is injected
         */
        async handleClick () {
            if (!this.filtersOpen && this.sortingEnabled) {
                this.$emit("toggleSort")
                if (this.resource) {
                    let sortString
                    if (this.isRelation && this.relation) {
                        if (this.sortByCount) {
                            let countParam = this.sortByCount ? "|count" : ""
                            sortString = `${this.relation}|relation${countParam}`
                        } else {
                            sortString = `${this.activeSortColumn}|relation:${this.relation}`
                        }
                    } else {
                        sortString = this.activeSortColumn
                    }
                    // Support legacy and new resources
                    // TODO: Deprecate in version 1.0
                    if (this.resource.SET_LIST_SORT) {
                        var sortMethod = "SET_LIST_SORT"
                    } else {
                        sortMethod = "setSort"
                    }
                    // eslint-disable-next-line no-console
                    console.warn("Missing sort action or mutation in resource")
                    if (this.computedSortDirection === "down") this.resource[sortMethod](`-${sortString}`)
                    else this.resource[sortMethod](sortString)
                    this.loading = true
                    await this.resource.getList()
                    this.loading = false
                }
            }
        },
        /**
         * On filter apply emits filterApply with selected options,
         * if a resource is injected writes the filter to state and refreshes the list
         */
        async handleFilterApply (data) {
            // eslint-disable-next-line no-console
            this.$emit("filterApply", data)
            if (!data || !this.resource) {
                this.filtersOpen = false
                if (this.$api && this.$api.FilterOptions) this.$api.FilterOptions.removeOptions(this.filterOptionsNamespace)
                return
            }
            if (data.filters.length === 0) {
                this.resource.removeFilter({
                    module: this.computedFilterModuleNamespace,
                    key: this.filterKey || this.activeFilterColumn
                })
            } else if (this.filterType === "list" || this.filterType === "userList") {
                let activeData = data.filters
                // Check if there are null values selected and if so remove them
                if (this.isPolymorphicRelation) {
                    let nullOptionIndex = activeData.findIndex(e => e.id === null)
                    if (nullOptionIndex > -1) {
                        var nullPresent = true
                        activeData.splice(nullOptionIndex, 1)
                    }
                } else {
                    let nullOptionIndex = activeData.indexOf(null)
                    if (nullOptionIndex > -1) {
                        nullPresent = true
                        activeData.splice(nullOptionIndex, 1)
                    }
                }
                if (this.isRelation) {
                    var params = {}
                    if (activeData.length > 0) {
                        params = { whereHas: [this.relation || this.activeFilterColumn, { whereIn: [this.relationKey, data.filters] }] }
                    }
                    if (nullPresent) {
                        params.doesntHave = [this.relation || this.activeFilterColumn]
                    }
                } else if (this.isPolymorphicRelation) {
                    params = {}
                    if (activeData.length > 0) {
                        let morphParams = {}
                        activeData.forEach(e => {
                            if (!morphParams[e.__model]) morphParams[e.__model] = []
                            morphParams[e.__model].push(e.id)
                        })
                        params = { whereHasMorphIn: [this.relation || this.activeFilterColumn, morphParams] }
                    }
                    if (nullPresent) {
                        params.doesntHaveMorph = [this.relation || this.activeFilterColumn, "*"]
                    }
                } else {
                    params = {}
                    if (activeData.length > 0) {
                        params.whereIn = [this.activeFilterColumn, data.filters]
                    }
                    if (nullPresent) {
                        params.orWhereNull = [this.activeFilterColumn]
                    }
                }
                this.resource.setFilter({
                    module: this.computedFilterModuleNamespace,
                    key: this.filterKey || this.activeFilterColumn,
                    params
                })
            } else if (this.filterType === "date") {
                let fullDateRangeSelected = (
                    this.filterData &&
                    formatDate(data.filters[0]) === formatDate(this.filterData[this.filterRemaps ? this.filterRemaps.min : "min"]) &&
                    formatDate(data.filters[1]) === formatDate(this.filterData[this.filterRemaps ? this.filterRemaps.max : "max"])
                )
                if (
                    data.filters && data.filters.length === 2 &&
                    !fullDateRangeSelected
                ) {
                    if (this.isRelation) {
                        params = {
                            whereHas: [this.relation || this.activeFilterColumn, { whereBetween: [this.relationKey, [formatDate(data.filters[0]), formatDate(data.filters[1]) + " 23:59:59.999"]] }]
                        }
                    } else {
                        params = {
                            whereBetween: [this.activeFilterColumn, [formatDate(data.filters[0]), formatDate(data.filters[1]) + " 23:59:59.999"]]
                        }
                    }
                    this.resource.setFilter({
                        module: this.computedFilterModuleNamespace,
                        key: this.filterKey || this.activeFilterColumn,
                        params
                    })
                } else if (fullDateRangeSelected) {
                    this.resource.removeFilter({
                        module: this.computedFilterModuleNamespace,
                        key: this.filterKey || this.activeFilterColumn
                    })
                }
            } else if (this.filterType === "number") {
                if (data.filters && data.filters.length === 2) {
                    if (this.isRelation) {
                        params = {
                            whereHas: [this.relation || this.activeFilterColumn, null, ">=", data.filters[0]],
                            has: [this.relation || this.activeFilterColumn, "<=", data.filters[1]]
                        }
                    } else {
                        params = {
                            whereBetween: [this.activeFilterColumn, [data.filters[0], data.filters[1]]]
                        }
                    }
                    this.resource.setFilter({
                        module: this.computedFilterModuleNamespace,
                        key: this.filterKey || this.activeFilterColumn,
                        params
                    })
                }
            }
            this.loading = true
            this.filtersOpen = false
            if (this.$api && this.$api.FilterOptions) this.$api.FilterOptions.removeOptions(this.filterOptionsNamespace)
            await this.resource.getList()
            this.loading = false
        },
        /**
         * Emits filtersOpen event and if a resource is injected fetches filter options
         */
        async openFilters () {
            this.$emit("filterOpen")
            if (!this.filterOptions && this.getFilterOptions) this.getFilterOptionsHandler()
            else if (!this.filterOptions && this.$api && this.$api.FilterOptions) {
                await this.$api.FilterOptions.getFilterOptions({
                    ...this.filterOptionsParams,
                    namespace: this.filterOptionsNamespace
                })
            }
            this.filtersOpen = true
        }
    }
}
</script>

<style lang="scss">
.uik-table-head-cell {
  cursor: default;
  .uik-table-head-cell__container {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
    text-align: left;
    width: 100%;
    padding: 15px 10px;
    height: 46px;
    font-size: 13px;
    color: rgba($textBase, 0.95);
    font-weight: 500;
    border-bottom: $borderDefault;
    transition: all 0.15s;
    &.uik-table-head-cell__align {
      &--center {
        justify-content: center;
        text-align: center;
        .uik-table-head-cell__sort-icon {
            margin-right: -13.5px;
        }
      }
      &--right {
        justify-content: flex-end;
        text-align: right;
      }
    }
    &.uik-table-head-cell__text {
      &--small {
        font-size: 12px;
        line-height: 14px;
      }
      &--base {
        color: $textBase;
      }
      &--fade {
        color: $textSecondary;
      }
      &--code {
        font-family: monospace;
      }
      &--bold {
        font-weight: 500;
      }
      &--primary {
        color: $primary;
      }
    }
    .uik-table-head-cell__content {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
    }
  }
  &:first-child {
    .uik-table-head-cell__container {
      padding-left: 20px;
    }
  }
  &:last-child {
    .uik-table-head-cell__container {
      padding-right: 20px;
    }
  }
  @media only #{$mediaDesktopSmall} {
    &__hide--desktop-small {
      display: none;
    }
  }
  @media only #{$mediaTablet} {
    &__hide--tablet {
      display: none;
    }
  }
  @media only #{$mediaMobile} {
    &__hide--mobile {
      display: none;
    }
  }
  &.uik-table-head-cell__checkbox {
    .uik-table-head-cell__container {
      .uik-table-head-cell__content {
        overflow: visible;
        .uik-checkbox__wrapper {
          transform: translateY(2px);
          transition: all 0.15s;
          &:hover {
              transform: scale(1.2) translateY(2px);
          }
          &:focus {
              transform: translateY(2px);
          }
          &:not(.uik-checkbox__toggle):not(.uik-checkbox__radio) {
            .uik-checkbox__label {
              display: flex;
              &::before {
                margin-right: 0;
                transform: none;
              }
            }
            .uik-checkbox__checkbox {
              &:checked + .uik-checkbox__label {
                .uik-checkbox__checkboxIcon {
                  top: 5px;
                }
              }
            }
          }
        }
      }
    }
  }
}
.uik-table-head {
  &--sticky {
    .uik-table-head-cell {
      position: sticky;
      top: 0;
      background: white;
      z-index: 2;
    }
  }
}
.uik-table-head-cell,
.uik-table-head .uik-table-head-cell,
.uik-table-head--sticky .uik-table-head-cell {
    &--clickable:not(.uik-table-head-cell--filters-open) {
        transition: all 0.15s;
        .uik-table-head-cell__container {
            position: relative;
            &:hover {
                background: darken($bg, 2%);
                // &::after {
                //     transition: width 0.25s, opacity 0s;
                //     opacity: 1;
                //     width: 100%;
                // }
                color: $textMain !important;
                .uik-table-head-cell__sort-icon {
                    opacity: 1;
                    color: $primary;
                }
            }
            &:active {
                background: darken($bg, 4%);
                .uik-table-head-cell__sort-icon {
                    transform: translateY(3px) scale(1.5);
                }
            }
        }
    }
    &--clickable, &--filterable {
        .uik-table-head-cell__container {
            &::after {
                content: "";
                position: absolute;
                width: 0%;
                height: 2px;
                background: $primary;
                opacity: 0.5;
                bottom: -1px;
                left: 0;
                right: 0;
                margin: 0 auto;
                transition: all 0.25s;
            }
        }
    }
    &--sortable {
        .uik-table-head-cell__container {
            &:hover {
                cursor: pointer;
            }
            .uik-table-head-cell__sort-icon {
                margin-left: 8px;
                height: 19px;
                transition: all 0.15s;
                color: $textBase;
                opacity: 0;
                svg {
                    font-size: 13px;
                }
                &--up {
                    transform: scaleY(-1);
                }
            }
        }
    }
    &--filterable {
        .uik-table-head-cell__container {
            position: relative;
            .uik-table-head-cell__filter-icon {
                display: flex;
                flex-flow: row nowrap;
                justify-content: center;
                align-items: center;
                position: absolute;
                right: 2px;
                border: solid 1px transparent;
                background: transparent;
                padding: 10px;
                padding-left: 5px;
                font-size: 13px;
                color: $textSecondary;
                transition: all 0.15s;
                border-radius: 4px;
                opacity: 0;
                transform: scale(0.5);
                &:hover {
                    cursor: pointer;
                    color: $primary;
                }
            }
            &:hover {
                .uik-table-head-cell__filter-icon {
                    transform: none;
                    opacity: 1;
                    &:hover {
                        transform: scale(1.15);
                        & + .uik-table-head-cell__sort-icon {
                            transform: scale(0.5);
                            opacity: 0;
                        }
                    }
                    &:active {
                        transform: none;
                    }
                }
            }
        }
    }
    &--filters-open {
        z-index: 100;
        .uik-table-head-cell__container {
            background: darken($bg, 2%);
            color: $textMain !important;
            &:hover {
                cursor: initial !important;
            }
            &::after {
                opacity: 1;
                width: 100%;
            }
            .uik-table-head-cell__filter-icon {
                transform: none;
                opacity: 1;
                color: $primary;
            }
        }
    }
    &--filter-active {
        .uik-table-head-cell__container {
            .uik-table-head-cell__filter-icon {
                transform: none;
                opacity: 1;
                color: $primary;
            }
        }
    }
    &--sort-active {
        .uik-table-head-cell__container {
            color: $textMain !important;
            .uik-table-head-cell__sort-icon {
                opacity: 1;
                color: $primary;
            }
            &:hover {
                .uik-table-head-cell__sort-icon {
                    transition: all 0.3s;
                    transform: scaleY(-1);
                    &--up {
                        transform: scaleY(1);
                    }
                }
            }
            &:active {
                .uik-table-head-cell__sort-icon {
                    transition: all 0.15s;
                    transform: translateY(-3px) scaleY(-1.5) scaleX(1.5);
                    &--up {
                        transform: translateY(3px) scale(1.5);
                    }
                }
            }
            &:focus {
                .uik-table-head-cell__sort-icon {
                    transition: all 0.15s;
                    transform: none;
                    &--up {
                        transform: scaleY(-1);
                    }
                }
                &:active {
                    .uik-table-head-cell__sort-icon {
                        transition: all 0.15s;
                        transform: translateY(-3px) scaleY(-1.5) scaleX(1.5);
                        &--up {
                            transform: translateY(3px) scale(1.5);
                        }
                    }
                }
            }
        }
    }
    &--loading {
        &.uik-table-head-cell--clickable {
            .uik-table-head-cell__container {
                &::after {
                    left: 0;
                    opacity: 1;
                    width: 0;
                    animation: table-head-cell-clickable-loading 2s linear infinite;
                    @keyframes table-head-cell-clickable-loading {
                        0% {
                            width: 0%;
                            margin-left: 0;
                        }
                        50% {
                            margin-left: 0;
                            width: 100%;
                        }
                        100% {
                            margin-left: 100%;
                            width: 0%;
                        }
                    }
                }
            }
        }
    }
    // Filters icon offset
    &--filterable .uik-table-head-cell__container:hover,
    &--filters-open .uik-table-head-cell__container,
    &--filter-active .uik-table-head-cell__container {
        transition: all 0.15s, padding 0.25s;
        padding-right: 35px;
        .uik-table-head-cell__sort-icon {
            margin-right: 0;
            transition: all 0.15s, margin-right 0.25s;
        }
    }
    @media only #{$mediaTablet} {
        &--clickable {
            .uik-table-head-cell__container {
                padding-right: 35px;
                .uik-table-head-cell__sort-icon {
                    transition: all 0.15s, margin-right 0.25s;
                }
                .uik-table-head-cell__filter-icon {
                    opacity: 1;
                    transform: none;
                }
                &:hover {
                    background: transparent !important;
                    &::after {
                        opacity: 0 !important;
                        width: 0 !important;
                    }
                }
            }
        }
        &--filterable.uik-table-head-cell--sort-active {
            .uik-table-head-cell__container {
                .uik-table-head-cell__sort-icon {
                    margin-right: 0;
                }
            }
        }
        &--sortable {
            .uik-table-head-cell__container {
                &:hover, &:focus {
                    background: darken($bg, 2%) !important;
                    &::after {
                        opacity: 1 !important;
                        width: 100% !important;
                    }
                    .uik-table-head-cell__sort-icon {
                        transition: all 0.15s !important;
                        transform: none !important;
                        &--up {
                            transform: scaleY(-1) !important;
                        }
                    }
                    &:active {
                        .uik-table-head-cell__sort-icon {
                            transition: all 0.15s !important;
                            transform: translateY(3px) scale(1.5) !important;
                            &--up {
                                transform: translateY(-3px) scaleY(-1.5) scaleX(1.5) !important;
                            }
                        }
                    }
                }
            }
        }
    }
}

</style>
